import { Divider, Form, Input, message, Spin, Table } from 'antd';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Breadcrumb from '../../../components/common/Breadcrumb/Breadcrumb';
import ButtonIconGhostSmall from '../../../components/common/ButtonIconGhost/ButtonIconGhostSmall';
import ButtonIconOutlineSmall from '../../../components/common/ButtonIconOutline/ButtonIconOutlineSmall';
import ButtonPrimaryMedium from '../../../components/common/ButtonPrimary/ButtonPrimaryMedium';
import ButtonSecondaryMedium from '../../../components/common/ButtonSecondary/ButtonSecondaryMedium';
import CheckboxComponent from '../../../components/common/CheckboxComponent/CheckboxComponent';
import InputComponent from '../../../components/common/InputComponent/InputComponent';
import SwitchComponent from '../../../components/common/SwitchComponent/SwitchComponent';
import Upload from '../../../components/common/Upload/Upload';
import { Icon } from '../../../components/icon/icon';
import { normFileWithValidation } from '../../../utils/file/file.utils';
import { cartesianProduct } from '../../../utils/array/cartesian.utils';
import ROUTER from '../../../routers/Routers';
import { variantTableReducer } from '../../../hooks/reducer/variantTableReducer';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts } from '../../../store/actions/product.action';
import SelectComponent from '../../../components/common/SelectComponent/SelectComponent';
import { getValueFromString } from '../../../utils/data/getValue.utils';
import { fromFormState } from './VariantData';
import callApi from '../../../utils/api/callAPI';
import { createSaleProduct } from '../../../store/actions/sale-product.action';
import { openMessageSuccess } from '../../../components/common/Message/openMessageSuccess';

const uploadImage = async file => {
  const formData = new FormData();
  formData.append('images', file);
  const res = await callApi({
    endpoint: 'sale_products/v3/upload_images',
    method: 'POST',
    data: formData,
  });
  return res.data.result.data[0];
};

const CreateProductInventoryPage = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [isDefault, setIsDefault] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [{ variantNames, variantValues }, dispatchVariant] = useReducer(
    variantTableReducer,
    {
      variantNames: [],
      variantValues: [],
    }
  );

  const products = useSelector(state => state.product.products);
  const createSaleProductState = useSelector(
    state => state.saleProduct.createSaleProduct
  );

  useEffect(() => {
    if (products.isNeedReloadData) dispatch(fetchProducts());
  }, [products.isNeedReloadData, dispatch]);

  const handleSubmit = useCallback(async () => {
    form
      .validateFields()
      .then(async values => {
        setIsLoading(true);
        const formData = new FormData();

        // Append simple string and boolean fields
        if (values.name) formData.append('name', values.name);
        if (values.product_id) formData.append('product_id', values.product_id);
        if (values.description)
          formData.append('description', values.description);
        if (values.hashtag) formData.append('hashtag', values.hashtag);

        if (!isNaN(Number(values.price_after)) && values.price_after)
          formData.append('price_after', values.price_after);
        if (!isNaN(Number(values.price_original)) && values.price_original)
          formData.append('price_original', values.price_original);
        if (!isNaN(Number(values.total_stock)) && values.total_stock)
          formData.append('price_after', values.total_stock);
        formData.append('sale_type', 'normal');
        formData.append('type_design', 'normal');
        formData.append('is_active', String(values.is_active ?? false));
        formData.append('is_new', String(values.is_new ?? false));
        formData.append('is_outside', String(false));
        formData.append('has_special_texture', String(false));
        formData.append(
          'is_apply_discount',
          String(values.is_apply_discount ?? false)
        );
        formData.append(
          'hash_tag',
          JSON.stringify(
            values.hash_tag?.split(',').map(tag => tag.trim()) || []
          )
        );

        // Append delivery type based on checkbox values
        // Force at least one delivery type to be selected due to form validation rules
        if (
          values.delivery_type?.fitta_delivery_and_installation &&
          values.delivery_type?.self_installation
        ) {
          formData.append('delivery_type', null);
        } else if (values.delivery_type?.fitta_delivery_and_installation) {
          formData.append('delivery_type', 0);
        } else if (values.delivery_type?.self_installation) {
          formData.append('delivery_type', 1);
        }

        if (values.thumbnail && values.thumbnail.length > 0) {
          const thumbnailFile = values.thumbnail[0];
          if (thumbnailFile.originFileObj) {
            formData.append('thumbnail', thumbnailFile.originFileObj);
          }
        }

        if (values.image && values.image.length > 0) {
          const imageFile = values.image[0];
          if (imageFile.originFileObj) {
            formData.append('image', imageFile.originFileObj);
          }
        }

        // Append variant data as JSON
        const variantIconsSettled = await Promise.allSettled(
          Object.entries(values.variant_icons || {}).map(
            async ([nameKey, valueMap]) => {
              const valueIconEntries = await Promise.allSettled(
                Object.entries(valueMap || {}).map(
                  async ([valueKey, icons]) => {
                    const uploadedUrls = await Promise.allSettled(
                      (icons || []).map(async file => {
                        try {
                          const url = await uploadImage(file.originFileObj);
                          return url || null;
                        } catch {
                          return null;
                        }
                      })
                    );
                    const urls = uploadedUrls
                      .filter(r => r.status === 'fulfilled' && r.value)
                      .map(r => r.value);
                    return [valueKey, urls];
                  }
                )
              );
              const valueIconMap = Object.fromEntries(
                valueIconEntries
                  .filter(r => r.status === 'fulfilled' && r.value)
                  .map(r => r.value)
              );
              return [nameKey, valueIconMap];
            }
          )
        );

        const variantIcons = Object.fromEntries(
          variantIconsSettled
            .filter(r => r.status === 'fulfilled' && r.value)
            .map(r => r.value)
        );
        const variantImagesSettled = await Promise.allSettled(
          Object.entries(values.variant_images || {}).map(
            async ([nameKey, valueMap]) => {
              const uploadedUrls = await Promise.allSettled(
                (valueMap || []).map(async file => {
                  try {
                    const url = await uploadImage(file.originFileObj);
                    return url || null;
                  } catch {
                    return null;
                  }
                })
              );
              const urls = uploadedUrls
                .filter(r => r.status === 'fulfilled' && r.value)
                .map(r => r.value);
              return [nameKey, urls];
            }
          )
        );

        const variantImages = Object.fromEntries(
          variantImagesSettled
            .filter(r => r.status === 'fulfilled' && r.value)
            .map(r => r.value)
        );
        if (values.variant_names) {
          const variants = fromFormState({
            variantNames: values.variant_names,
            variantValues: values.variant_values,
            variantIcons: variantIcons,
            variantImages: variantImages,
            quantity: values.quantity,
            price: values.price,
            discount: values.discount,
            isDefault,
          }).filter(
            item => item.price || item.quantity || item.discount || item.images
          ); // Lọc ra những variant có giá và số lượng hợp lệ
          formData.append('variants', JSON.stringify(variants));
        }

        const action = await dispatch(createSaleProduct(formData));
        if (action.payload?.data) {
          openMessageSuccess({ content: t('Product created successfully') });
        } else {
          message.error(t('Product creation failed. Please try again.'));
        }
        setIsLoading(false);
      })
      .catch(() => {});
  }, [isDefault, form, dispatch, t]);

  useEffect(() => {
    if (variantNames.length) {
      form.setFieldsValue({
        price_after: undefined,
        price_origin: undefined,
        total_stock: undefined,
      });
    }
  }, [variantNames]);

  return (
    <>
      <div className="flex flex-col items-start gap-0 self-stretch shrink-0 h-full overflow-auto">
        <header className="flex p-16 items-center justify-between self-stretch sticky left-0 right-0 top-0 bg-fills-white z-1000 shadow-dropshadow2">
          <Breadcrumb
            className="lg:flex hidden"
            items={[
              {
                title: (
                  <span className="text-title-medium text-black-low">
                    {t('productInventoryRetail')}
                  </span>
                ),
                href: ROUTER.CMS + ROUTER.PRODUCT_INVENTORY,
              },
              {
                title: (
                  <span className="text-title-medium-emphasized text-black-high">
                    {t('createProduct')}
                  </span>
                ),
              },
            ]}
          />
          <ButtonPrimaryMedium
            title={t('createProduct')}
            posIcon={<Icon.ICAddCircle />}
            onClick={handleSubmit}
          />
        </header>
        <Form
          form={form}
          layout="vertical"
          className="flex items-start gap-24 grow self-stretch p-16"
        >
          <div className="flex flex-col items-start gap-16 grow">
            <RenderGeneralInfo />
            <RenderShippingMethod form={form} />
            <RenderProductMedia form={form} />
            <RenderProductVariant
              form={form}
              isDefault={isDefault}
              setIsDefault={setIsDefault}
              variantNames={variantNames}
              variantValues={variantValues}
              dispatchVariant={dispatchVariant}
            />
          </div>
          <div className="flex w-[400px] flex-col items-start gap-16">
            <RenderOptionsDisplay />
            <RenderEntryHashtag />
          </div>
        </Form>
      </div>
      <Spin size={'large'} fullscreen={true} spinning={isLoading} />
    </>
  );
};

export default CreateProductInventoryPage;

const RenderOptionsDisplay = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-fills-white flex flex-col p-20 items-start gap-16 self-stretch rounded-[16px]">
      <h2 className="text-title-medium-emphasized text-black-medium">
        {t('Display')}
      </h2>
      <div className="flex flex-col items-start gap-16 self-stretch">
        <Form.Item
          name="is_active"
          layout="horizontal"
          label={t('Show on website')}
          className="self-stretch"
          colon={false}
        >
          <SwitchComponent defaultChecked={false} />
        </Form.Item>
        <Form.Item
          name="is_new"
          layout="horizontal"
          label={t('Mark as new arrival')}
          className="self-stretch"
          colon={false}
        >
          <SwitchComponent defaultChecked={false} />
        </Form.Item>
        <Form.Item
          name="is_apply_discount"
          layout="horizontal"
          label={t('Apply Promotion')}
          className="self-stretch"
          colon={false}
        >
          <SwitchComponent defaultChecked={false} />
        </Form.Item>
      </div>
    </div>
  );
};

const RenderEntryHashtag = () => {
  const { t } = useTranslation();
  return (
    <div className="bg-fills-white flex p-20 items-start flex-col gap-16 self-stretch rounded-[16px]">
      <h2 className="text-title-medium-emphasized text-black-medium">
        {t('Hashtag')}
      </h2>
      <Form.Item name="hash_tag" className="self-stretch">
        <Input.TextArea placeholder={t('Enter hashtag')} />
      </Form.Item>
    </div>
  );
};

const RenderGeneralInfo = () => {
  const { t } = useTranslation();
  const products = useSelector(state => state.product.products);

  return (
    <section className="flex p-20 flex-col items-start gap-16 self-stretch rounded-[16px] bg-fills-white">
      <h2 className="text-title-medium-emphasized text-black-medium">
        {t('General information')}
      </h2>
      <div className="grid grid-cols-2 gap-16 self-stretch">
        <Form.Item
          label={t('Product name')}
          name="name"
          rules={[{ required: true, message: t('Please enter product name') }]}
        >
          <InputComponent placeholder={t('Enter product name')} size="large" />
        </Form.Item>
        <Form.Item
          label={t('Product type')}
          name="product_id"
          rules={[{ required: true, message: t('Please select product type') }]}
        >
          <SelectComponent
            options={[
              ...products?.data?.map(item => ({
                label: getValueFromString(item.name),
                value: item.id,
              })),
            ]}
            placeholder={t('Select product type')}
            showSearch={true}
          />
        </Form.Item>
      </div>
      <Form.Item
        label={t('Product description')}
        name="description"
        className="self-stretch"
      >
        <Input.TextArea placeholder={t('Enter product description')} />
      </Form.Item>
    </section>
  );
};

const RenderShippingMethod = ({ form }) => {
  const { t } = useTranslation();
  return (
    <section className="flex p-20 flex-col items-start gap-24 self-stretch rounded-[16px] bg-fills-white">
      <h2 className="text-title-medium-emphasized text-black-medium">
        {t('Available delivery methods')}
      </h2>

      <div className="flex flex-col gap-16 items-start justify-start">
        <Form.Item
          name={['delivery_type', 'fitta_delivery_and_installation']}
          valuePropName="checked"
          className="self-stretch"
          rules={[
            {
              validator: (_, value) => {
                const deliveryAndInstallation = value || false;
                const selfInstallation =
                  form.getFieldValue(['delivery_type', 'self_installation']) ||
                  false;

                if (!deliveryAndInstallation && !selfInstallation) {
                  return Promise.reject(
                    new Error(t('Please select at least one delivery method'))
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <CheckboxComponent>
            <span className="text-label-large">
              {t('Fitta delivery & installation')}
            </span>
          </CheckboxComponent>
        </Form.Item>
        <Form.Item
          name={['delivery_type', 'self_installation']}
          valuePropName="checked"
          className="self-stretch"
          rules={[
            {
              validator: (_, value) => {
                const deliveryAndInstallation =
                  form.getFieldValue([
                    'delivery_type',
                    'fitta_delivery_and_installation',
                  ]) || false;
                const selfInstallation = value || false;

                if (!deliveryAndInstallation && !selfInstallation) {
                  return Promise.reject(
                    new Error(t('Please select at least one delivery method'))
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <CheckboxComponent>
            <span className="text-label-large">
              {t('Customer self-installation')}
            </span>
          </CheckboxComponent>
        </Form.Item>
      </div>
    </section>
  );
};

const RenderProductMedia = ({ form }) => {
  const { t } = useTranslation();
  const watchThumbnail = Form.useWatch('thumbnail', form);
  const watchImage = Form.useWatch('image', form);
  // const watchImages = Form.useWatch('images', form);

  return (
    <section className="flex p-24 flex-col items-start gap-24 self-stretch rounded-[16px] bg-fills-white">
      <h2 className="text-title-medium-emphasized">{t('Images & Media')}</h2>
      <Form.Item
        label={
          <div className="flex items-center gap-8">
            <span className="">{t('Thumbnail image')}</span>
            <span className="text-body-small text-black-low">
              {t('(3:2 ratio image, under 1MB)')}
            </span>
          </div>
        }
        className="self-stretch"
      >
        <div className="flex items-center gap-24 justify-start mt-10">
          <Form.Item
            name="thumbnail"
            valuePropName="fileList"
            getValueFromEvent={e => normFileWithValidation({ e, oldFiles: [] })}
          >
            <div className="flex flex-wrap items-center gap-16">
              {watchThumbnail && (
                <div className="relative w-64 h-64">
                  <ButtonIconGhostSmall
                    icon={<Icon.ICDelete />}
                    onClick={() => {
                      form.setFieldValue('thumbnail', null);
                    }}
                    className="absolute top-0 right-0"
                  />
                  <img
                    src={watchThumbnail[0]?.url || watchThumbnail[0]?.thumbUrl}
                    alt=""
                    className="w-64 h-64 object-cover rounded-[4px]"
                  />
                </div>
              )}
              <Upload className="flex w-64 h-64 p-16">
                <span className="flex w-32 h-32 items-center justify-center">
                  <Icon.ICAddImage />
                </span>
              </Upload>
            </div>
          </Form.Item>
          <Divider type="vertical" className="!h-64" />
          <Form.Item
            name="image"
            valuePropName="fileList"
            getValueFromEvent={e => normFileWithValidation({ e, oldFiles: [] })}
          >
            <div className="flex flex-wrap items-center gap-16">
              {watchImage && (
                <div className="relative w-64 h-64">
                  <ButtonIconGhostSmall
                    icon={<Icon.ICDelete />}
                    onClick={() => {
                      form.setFieldValue('image', null);
                    }}
                    className="absolute top-0 right-0"
                  />
                  <img
                    src={watchImage[0]?.url || watchImage[0]?.thumbUrl}
                    alt=""
                    className="w-64 h-64 object-cover rounded-[4px]"
                  />
                </div>
              )}
              <Upload className="flex w-64 h-64 p-16">
                <span className="flex w-32 h-32 items-center justify-center">
                  <Icon.ICAddImage />
                </span>
              </Upload>
            </div>
          </Form.Item>
        </div>
      </Form.Item>
      {/* <Form.Item
        label={
          <div className="flex items-center gap-8">
            <span className="">Thư viện ảnh</span>
            <span className="text-body-small text-black-low">
              (Ảnh tỉ lệ 3:2 dung lượng dưới 1MB)
            </span>
          </div>
        }
        name="images"
        className="self-stretch"
        valuePropName="fileList"
        getValueFromEvent={e =>
          normFileWithValidation({ e, oldFiles: watchImages })
        }
      >
        <div className="flex flex-wrap items-center gap-16">
          {watchImages &&
            watchImages.map((item, index) => {
              return (
                <div key={index} className="relative w-64 h-64">
                  <ButtonIconGhostSmall
                    icon={<Icon.ICDelete />}
                    onClick={() => {
                      const newImages = [...watchImages];
                      newImages.splice(index, 1);
                      form.setFieldValue('images', newImages);
                    }}
                    className="absolute top-0 right-0"
                  />
                  <img
                    src={item.url || item.thumbUrl}
                    alt=""
                    className="w-64 h-64 object-cover rounded-[4px]"
                  />
                </div>
              );
            })}
          <Upload multiple={true} className="flex w-64 h-64 p-16">
            <span className="flex w-32 h-32 items-center justify-center">
              <Icon.ICAddImage />
            </span>
          </Upload>
        </div>
      </Form.Item> */}
    </section>
  );
};

const RenderPriceInfo = () => {
  const { t } = useTranslation();
  return (
    <section className="flex p-20 flex-col items-start gap-24 self-stretch rounded-[16px] bg-fills-white">
      <h2 className="text-title-medium-emphasized text-black-medium">
        {t('Sales information')}
      </h2>
    </section>
  );
};

const RenderProductVariant = ({
  form,
  isDefault,
  setIsDefault,
  variantNames,
  variantValues,
  dispatchVariant,
}) => {
  const { t } = useTranslation();

  const defaultColumns = useMemo(
    () => [
      {
        title: t('Quantity'),
        dataIndex: 'quantity',
        key: 'quantity',
        width: 160,
        render: (_, record) => {
          return (
            <div className="flex p-16 pr-24 items-center gap-16 grow self-stretch">
              <Form.Item
                // rules={[{ required: 'true', message: 'Vui lòng nhập số lượng' }]}
                name={['quantity', record.key]}
                className="!mb-0"
              >
                <InputComponent
                  placeholder={t('Enter quantity')}
                  size="large"
                />
              </Form.Item>
            </div>
          );
        },
      },
      {
        title: t('Price'),
        dataIndex: 'price',
        key: 'price',
        width: 160,
        render: (_, record) => {
          return (
            <div className="flex p-16 pr-24 items-center gap-16 grow self-stretch">
              <Form.Item
                // rules={[{ required: 'true', message: 'Vui lòng nhập giá' }]}
                name={['price', record.key]}
                className="!mb-0"
              >
                <InputComponent placeholder={t('Enter price')} size="large" />
              </Form.Item>
            </div>
          );
        },
      },
      {
        title: t('Discount'),
        dataIndex: 'discount',
        key: 'discount',
        width: 160,
        render: (_, record) => {
          return (
            <div className="flex p-16 pr-24 items-center gap-16 grow self-stretch">
              <Form.Item
                // rules={[{ required: 'true', message: 'Vui lòng nhập giảm giá' }]}
                name={['discount', record.key]}
                className="!mb-0"
              >
                <InputComponent
                  placeholder={t('Enter discount')}
                  size="large"
                />
              </Form.Item>
            </div>
          );
        },
      },
    ],
    [t]
  );

  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);

  const watchVariantNames = Form.useWatch('variant_names');
  const watchVariantValues = Form.useWatch('variant_values');
  const watchVariantIcon = Form.useWatch('variant_icons', form);
  const watchVariantImage = Form.useWatch('variant_images', form);

  const tableField = ['variant_images', 'quantity', 'price', 'discount'];

  const mapVariantToColumns = useCallback(() => {
    if (!watchVariantNames || !watchVariantValues) return;

    const names = Object.entries(watchVariantNames).filter(([_, name]) => name);
    const lengthValues = Object.entries(watchVariantValues)
      .map(([key, value]) => {
        const count = Object.values(value || {}).filter(Boolean).length;
        return count > 0 ? [key, count] : null;
      })
      .filter(Boolean);

    const newColumns = names.map(([key, value], index) => {
      const span = lengthValues.reduce(
        (total, [vKey, vCount]) =>
          Number(vKey) > Number(key) ? total * vCount : total,
        1
      );

      return {
        title: value,
        dataIndex: key,
        key,
        onCell: record => {
          const keys = record.key.split('-');
          const keyNum = Number(key);
          const isTopRow = keys.slice(keyNum).every(k => k === '1');
          return {
            rowSpan: span > 1 ? (isTopRow ? span : 0) : 1,
            className: 'border-[0.5px]',
          };
        },
        render: (text, record) => {
          if (index === names.length - 1) {
            return (
              <div className="p-16 pr-24 flex flex-col items-start justify-start">
                <span className="text-body-medium-emphasized self-stretch grow">
                  {text}
                </span>
                <Form.Item
                  name={['variant_images', `${record.key}`]}
                  valuePropName="fileList"
                  getValueFromEvent={e =>
                    normFileWithValidation({ e, oldFiles: [] })
                  }
                >
                  <div className="flex flex-wrap items-center gap-16">
                    {watchVariantImage?.[`${record.key}`] ? (
                      <div className="relative w-64 h-64">
                        <ButtonIconGhostSmall
                          icon={<Icon.ICDelete />}
                          onClick={() => {
                            form.setFieldValue(
                              ['variant_images', `${record.key}`],
                              undefined
                            );
                          }}
                          className="absolute top-0 right-0"
                        />
                        <img
                          src={
                            watchVariantImage?.[`${record.key}`]?.[0]?.url ||
                            watchVariantImage?.[`${record.key}`]?.[0]?.thumbUrl
                          }
                          alt=""
                          className="w-64 h-64 object-cover rounded-[4px]"
                        />
                      </div>
                    ) : (
                      <Upload className="flex w-64 h-64 p-16">
                        <span className="flex w-32 h-32 items-center justify-center">
                          <Icon.ICAddImage />
                        </span>
                      </Upload>
                    )}
                  </div>
                </Form.Item>
              </div>
            );
          }
          return (
            <div className="p-16 pr-24 flex items-center justify-start text-left text-body-medium-emphasized self-stretch grow">
              {text}
            </div>
          );
        },
      };
    });
    setColumns([...newColumns, ...defaultColumns]);
  }, [
    watchVariantNames,
    watchVariantValues,
    watchVariantImage,
    defaultColumns,
  ]);

  const mapVariantToDataSource = useCallback(() => {
    if (!watchVariantNames || !watchVariantValues) {
      setData([]);
      return;
    }

    const valuesArray = Object.entries(watchVariantValues)
      .filter(([_, values]) => values)
      .map(([key, values]) => ({
        key,
        values: Object.entries(values)
          .filter(([_, vValue]) => vValue)
          .map(([vKey, vValue]) => ({
            key: vKey,
            value: vValue,
          })),
      }))
      .filter(item => item.values.length > 0);

    const combinations = cartesianProduct(
      valuesArray.map(v =>
        v.values.map(value => ({
          key: `${v.key}`,
          id: value.key,
          value: value.value,
        }))
      )
    );

    const newDataSource = combinations.map((combo, index) => {
      const row = {
        id: index,
        key: `${combo.map(item => item.id).join('-')}`,
      };

      combo.forEach(item => {
        row[item.key] = item.value;
      });

      return row;
    });

    setData(newDataSource);
  }, [watchVariantNames, watchVariantValues]);

  useEffect(() => {
    mapVariantToColumns();
    mapVariantToDataSource();
  }, [
    watchVariantNames,
    watchVariantValues,
    watchVariantImage,
    mapVariantToColumns,
    mapVariantToDataSource,
  ]);

  return (
    <section className="flex p-24 flex-col items-start gap-24 self-stretch rounded-[16px] bg-fills-white">
      <h2 className="text-title-medium-emphasized">{t('Sales information')}</h2>
      {!variantNames?.length && (
        <div className="flex flex-col gap-16 items-start justify-start self-stretch">
          <Form.Item
            label={t('Product price')}
            name={'price_original'}
            className="self-stretch"
          >
            <InputComponent
              size="large"
              placeholder={t('Product price')}
              addonBefore={'đ'}
            />
          </Form.Item>
          <Form.Item
            label={t('Discount')}
            name={'price_after'}
            className="self-stretch"
          >
            <InputComponent
              size="large"
              placeholder={t('Price after discount')}
              addonBefore={'đ'}
            />
          </Form.Item>
          <Form.Item
            label={t('Stock')}
            name={'total_stock'}
            className="self-stretch"
          >
            <InputComponent size="large" placeholder={t('Stock')} />
          </Form.Item>
        </div>
      )}
      <div className="flex flex-col items-center gap-16 self-stretch">
        {variantNames.map((sttName, index) => (
          <div
            key={sttName}
            className="flex p-16 flex-col gap-16 self-stretch rounded-[8px] bg-fills-primary-varient"
          >
            <div className="flex items-center justify-between self-stretch">
              <h3 className="text-title-small-emphasized">
                {t('Attribute')} {index + 1}
              </h3>
              <ButtonIconOutlineSmall
                icon={<Icon.ICDelete />}
                onClick={() => {
                  dispatchVariant({
                    type: 'SUB_VARIANT_NAME',
                    payload: { index },
                  });

                  form.resetFields([
                    ['variant_names', `${sttName}`],
                    ['variant_values', `${sttName}`],
                    ...tableField,
                  ]); // Xóa dữ liệu khi xóa thuộc tính
                  setIsDefault(null); // Reset mặc định khi xóa thuộc tính
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-24 items-start self-stretch">
              <Form.Item
                label={t('Attribute')}
                name={['variant_names', `${sttName}`]}
              >
                <InputComponent
                  placeholder={t('Enter attribute name')}
                  size="large"
                />
              </Form.Item>
              <Form.Item label={t('Value')}>
                <div className="flex flex-col items-start justify-start self-stretch grow gap-8">
                  {variantValues[index].map((sttValue, valueIndex) => (
                    <div
                      key={`${sttName} ${sttValue}`}
                      className="flex items-center justify-start self-stretch grow gap-4"
                    >
                      <Form.Item
                        name={['variant_values', `${sttName}`, `${sttValue}`]}
                        className="self-stretch grow"
                      >
                        <InputComponent
                          placeholder={t('Enter attribute value')}
                          size="large"
                          onKeyDown={e => {
                            e.stopPropagation();
                            if (e.key === 'Enter') {
                              dispatchVariant({
                                type: 'ADD_VARIANT_VALUE',
                                payload: { index },
                              });
                            }
                          }}
                          autoFocus={
                            valueIndex === variantValues[index].length - 1
                          } // Focus input mới
                        />
                      </Form.Item>
                      <Form.Item
                        name={['variant_icons', `${sttName}`, `${sttValue}`]}
                        valuePropName="fileList"
                        getValueFromEvent={e =>
                          normFileWithValidation({ e, oldFiles: [] })
                        }
                      >
                        <div className="flex flex-wrap items-center gap-16">
                          {watchVariantIcon?.[`${sttName}`]?.[`${sttValue}`] ? (
                            <div className="relative w-40 h-40">
                              <ButtonIconGhostSmall
                                icon={<Icon.ICDelete />}
                                onClick={() => {
                                  form.setFieldValue(
                                    [
                                      'variant_icons',
                                      `${sttName}`,
                                      `${sttValue}`,
                                    ],
                                    undefined
                                  );
                                }}
                                className="absolute top-0 right-0"
                              />
                              <img
                                src={
                                  watchVariantIcon?.[`${sttName}`]?.[
                                    `${sttValue}`
                                  ]?.[0]?.url ||
                                  watchVariantIcon?.[`${sttName}`]?.[
                                    `${sttValue}`
                                  ]?.[0]?.thumbUrl
                                }
                                alt=""
                                className="w-40 h-40 object-cover rounded-[4px]"
                              />
                            </div>
                          ) : (
                            <Upload className="flex w-40 h-40 p-16">
                              <span className="flex w-24 h-24 items-center justify-center">
                                <Icon.ICAddImage />
                              </span>
                            </Upload>
                          )}
                        </div>
                      </Form.Item>
                      {1 !== variantValues[index].length && (
                        <ButtonIconGhostSmall
                          icon={<Icon.ICClose />}
                          onClick={() => {
                            dispatchVariant({
                              type: 'SUB_VARIANT_VALUE',
                              payload: { index, valueIndex },
                            });
                            form.resetFields([
                              ['variant_values', `${sttName}`, `${sttValue}`],
                              ['variant_icons', `${sttName}`, `${sttValue}`],
                              ...tableField,
                            ]); // Xóa icon khi xóa value
                            setIsDefault(null); // Reset mặc định khi xóa thuộc tính
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </Form.Item>
            </div>
          </div>
        ))}

        {variantNames.length < 3 && (
          <ButtonSecondaryMedium
            posIcon={<Icon.ICAdd />}
            title={t('Add new variant')}
            onClick={() => {
              dispatchVariant({ type: 'ADD_VARIANT_NAME' });
              form.resetFields(tableField); // Xóa icon khi xóa value
              setIsDefault(null); // Reset mặc định khi xóa thuộc tính
            }}
          />
        )}
        <Divider className="!p-0 !my-0 w-full" />
        <h2 className="flex self-stretch text-title-medium-emphasized text-left">
          {t('Variant list')}
        </h2>
        {/* <Form.Item
          name="setAllPrices"
          valuePropName="checked"
          className="self-stretch"
        >
          <CheckboxComponent>
            <span className="text-label-large">
              Áp dụng giá chung cho thuộc tính 1
            </span>
          </CheckboxComponent>
        </Form.Item> */}
        <Table
          columns={columns}
          dataSource={data}
          pagination={false}
          rowKey="key"
          rowSelection={{
            type: 'radio',
            columnWidth: 48,
            onChange: keys => {
              setIsDefault(keys[0]);
            },
            columnTitle: t('Default'),
          }}
        />
      </div>
    </section>
  );
};
